import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { connect, useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import cx from 'classnames'
import { isEqual, omit } from 'lodash'
import produce from 'immer'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinnerThird } from '@fortawesome/pro-solid-svg-icons'
import { useToasts } from 'react-toast-notifications'

import InfoHover from '@sweetspot/sweetspot-js/common/components/InfoHover'
import TextInputField from '@sweetspot/sweetspot-js/common/components/FormElements/TextInputField'
import ToggleSwitchControlled from '@sweetspot/sweetspot-js/common/components/ToggleSwitchControlled'
import ToggleButton from '@sweetspot/sweetspot-js/common/components/FormElements/ToggleButton'
import styles from './styles.module.scss'

// Utils
import GetTranslatedString from '@sweetspot/club-portal-legacy/helpers/GetTranslatedString'
import InputSelectDateSimple from '@sweetspot/sweetspot-js/ui-kit/components/InputSelectDateSimple'
import moment from 'moment-timezone'
import Label from '@sweetspot/sweetspot-js/common/components/FormElements/Partials/Label'
import { useTranslation } from 'react-i18next'
import { toggleGitClubSelectable } from '@sweetspot/shared/data-access/api-platform'
import { useMutation } from 'react-query'
import MigrateButton from '@sweetspot/club-portal-legacy/components/Wizard/containers/membership/GeneralSettings/MigrateButton'
import {
  AUTO_RENEWAL_EMAIL_TO_MEMBER_MIN_MAX_DAYS,
  AUTO_RENEWAL_LAST_CANCELLATION_MIN_MAX_DAYS,
  MAX_MEMBERSHIP_SEATS_ALLOWEED,
} from '@sweetspot/club-portal-legacy/modals/MembershipWizardModal/validation'
import { FlagNames, useFlag } from '@sweetspot/shared/util/feature-flag'
import DropdownSelect from '@sweetspot/club-portal-legacy/components/DropdownSelect'
import { Button, RadioGroup } from '@sweetspot/scramble-ds/atoms'

const DESCRIPTION_MAX_LENGTH = 500

/**
 * Controlled wrapper
 */
const GeneralSettings = ({
  currentMembership,
  lang,
  values,
  errors,
  setSingleEntityValue,
  onSetMembershipValue,
  onCloseModal,
  disabled,
  onlyFields,
  loading,
  handleOnSave,
  migrateMembershipAutorenewalSettings,
}) => {
  const prevLoadingState = useRef()
  const currentClub = useSelector((state) => {
    return state?.golfClub?.list.find((club) => club.id === state?.golfClub?.selectedId)
  })
  const isGitClubSelectEnabled = useFlag(FlagNames.GitClubSelect)

  const hasCdhNumber = !!currentClub?.cdh_id

  const TRANSLATIONS = {
    nameOfMembership: GetTranslatedString(
      lang,
      values.type === 'membership'
        ? 'membership.options.nameOfMembership'
        : 'membership.options.nameOfPass'
    ),
    name: GetTranslatedString(
      lang,
      values.type === 'membership' ? 'wizard.membershipName' : 'wizard.passName'
    ),
    hiddenInApp: GetTranslatedString(lang, 'membership.settings.hiddenInApp'),
    duration: GetTranslatedString(lang, 'wizard.duration'),
    annual: GetTranslatedString(lang, 'membership.options.calendarYear'),
    twelve_months: GetTranslatedString(lang, 'membership.options.twelveMonths'),
    gitSync: GetTranslatedString(lang, hasCdhNumber ? 'wizard.cdhSync' : 'wizard.gitSync'),
    active: GetTranslatedString(lang, 'settings.active'),
    inactive: GetTranslatedString(lang, 'settings.inactive'),
    membershipStatus: GetTranslatedString(
      lang,
      values.type === 'membership' ? 'wizard.membershipStatus' : 'wizard.passStatus'
    ),
    enableSSPayment: GetTranslatedString(lang, 'wizard.enableSSPayment'),
    enabled: GetTranslatedString(lang, 'enabled'),
    disabled: GetTranslatedString(lang, 'disabled'),
    required: GetTranslatedString(lang, 'required'),
    playRight: GetTranslatedString(lang, 'playRight'),
    create: GetTranslatedString(lang, 'create'),
    save: GetTranslatedString(lang, 'save'),
    payment: GetTranslatedString(lang, 'payment'),
    description: GetTranslatedString(lang, 'settings.description'),
    period: GetTranslatedString(lang, 'membership.options.period'),
    startDate: GetTranslatedString(lang, 'membership.options.startDate'),
    endDate: GetTranslatedString(lang, 'membership.options.endDate'),
    signupDate: GetTranslatedString(lang, 'membership.options.signupDate'),
    durationApplied: GetTranslatedString(lang, 'membership.options.durationApplied'),
    noEnd: GetTranslatedString(lang, 'membership.options.noEnd'),
    chooseDate: GetTranslatedString(lang, 'membership.options.chooseDate'),
  }

  const { t } = useTranslation()
  const { addToast } = useToasts()
  const [showActionButtons, setShowActionButtons] = useState(false)
  const [isMaxSeatsToggleEnabled, setIsMaxSeatsToggleEnabled] = useState(
    values?.membership_member_limit?.is_active || false
  )
  const [isAgeLimitToggleEnabled, setIsAgeLimitToggleEnabled] = useState(false)
  const [isNoEnd, setIsNoEnd] = useState()
  const [descriptionText, setDescriptionText] = useState(values.description || '')
  const [enableGitCLubSelect, setEnableGitCLubSelect] = useState(values.is_git_club_selectable)
  const [maxSeatsValue, setMaxSeatsValue] = useState(undefined)
  const [fromAge, setFromAge] = useState(null)
  const [fromAgeError, setFromAgeError] = useState(false)
  const [toAgeError, setToAgeError] = useState(false)
  const [toAge, setToAge] = useState(null)
  const [numberOfCurrentMembers, setNumberOfCurrentMembers] = useState(
    values?.membership_member_limit?.current_members || null
  )
  const [membershipValue, setMembershipValue] = useState(values)
  const [subscriptionName, setSubscriptionName] = useState(values?.name || '')
  const [isLoading, setIsLoading] = useState(false)

  const [shouldRenderUpdatedUI, setShouldRenderUpdatedUI] = useState(false)

  useEffect(() => {
    setMembershipValue(values)
  }, [values])

  useEffect(() => {
    setSubscriptionName(values?.name || '')
  }, [values?.name])

  const isThereAnyError = useMemo(() => {
    return Object.values(errors).some((errorText) => errorText.length > 0)
  }, [errors])

  useEffect(() => {
    if (
      (prevLoadingState.current !== loading && prevLoadingState.current === true) ||
      isThereAnyError
    ) {
      setIsLoading(false)
    }

    if (isThereAnyError) onCloseModal()

    prevLoadingState.current = loading
  }, [loading, errors, isThereAnyError])

  const isAnyMembershipValueUpdated = useMemo(() => {
    // Drop the name and type fields from the comparison because they are handled separately
    const filteredMembershipValue = omit(membershipValue, ['name', 'type'])
    const filteredValues = omit(values, ['name', 'type'])
    if (isLoading) return true

    return !isEqual(filteredMembershipValue, filteredValues)
  }, [values, membershipValue, isLoading])

  useEffect(() => {
    if (isAnyMembershipValueUpdated) {
      const timeout = setTimeout(() => {
        setShouldRenderUpdatedUI(true)
      }, 50)

      return () => clearTimeout(timeout)
    } else {
      // Reset if it's not updated anymore
      setShouldRenderUpdatedUI(false)
    }
  }, [isAnyMembershipValueUpdated])

  const handleMembershipValueChange = useCallback((key, value) => {
    setMembershipValue(
      produce((draft) => {
        draft[key] = value
      })
    )
  }, [])

  const handleSaveBtnClick = () => {
    setIsLoading(true)
    onSetMembershipValue(membershipValue)
  }

  const MAX_AGE = 99
  const MIN_AGE = 0

  const toggleGitClubSelect = useMutation({
    mutationFn: () => {
      setEnableGitCLubSelect(!enableGitCLubSelect)
      return toggleGitClubSelectable(values.id)
    },
    onSuccess: (data) => setEnableGitCLubSelect(data.is_git_club_selectable),
    onError: () => setEnableGitCLubSelect(!enableGitCLubSelect),
  })

  const setDurationValue = (key, value) => {
    let { options: durationOptions } = membershipValue.duration

    if (value === true && !durationOptions.includes(key)) {
      handleMembershipValueChange('duration', {
        ...membershipValue.duration,
        options: [...durationOptions, key],
        from:
          key === 'period'
            ? membershipValue.duration.from ?? moment.utc().startOf('day').format()
            : membershipValue.duration.from,
      })
    } else if (value === false) {
      handleMembershipValueChange('duration', {
        ...membershipValue.duration,
        options: durationOptions.filter((item) => item !== key),
        from: key === 'period' ? null : membershipValue.duration.from,
        to: key === 'period' ? null : membershipValue.duration.to,
      })
    }
  }

  useEffect(() => {
    setIsNoEnd(membershipValue.duration.to === null)
  }, [membershipValue?.duration?.to])

  useEffect(() => {
    const memberLimit = membershipValue?.membership_member_limit
    const maxMembers = memberLimit?.max_members

    if (maxMembers !== undefined && maxMembers >= 0 && maxMembers < 10000) {
      setMaxSeatsValue(maxMembers)
    }
  }, [membershipValue?.membership_member_limit?.max_members])

  useEffect(() => {
    const memberLimit = membershipValue?.membership_member_limit
    const currentMembers = memberLimit?.current_members

    if (
      currentMembers !== undefined &&
      currentMembers !== numberOfCurrentMembers &&
      memberLimit?.is_active
    ) {
      setNumberOfCurrentMembers(currentMembers)
    }
  }, [membershipValue?.membership_member_limit])

  useEffect(() => {
    setIsMaxSeatsToggleEnabled(membershipValue?.membership_member_limit?.is_active)
  }, [membershipValue?.membership_member_limit?.is_active])

  useEffect(() => {
    setDescriptionText(membershipValue?.description || '')
  }, [membershipValue?.description])

  useEffect(() => {
    const { min, max } = membershipValue?.membership_age_range_limit || {}
    if (min >= 0 && max >= 0 && max > min) {
      setFromAge(min)
      setToAge(max)
      setIsAgeLimitToggleEnabled(true)
    } else {
      setIsAgeLimitToggleEnabled(false)
    }
  }, [
    membershipValue?.membership_age_range_limit,
    membershipValue?.membership_age_range_limit?.min,
    membershipValue?.membership_age_range_limit?.max,
  ])

  useEffect(() => {
    if (toAge > fromAge && toAge <= MAX_AGE && fromAge >= MIN_AGE) {
      handleMembershipValueChange('membership_age_range_limit', { min: fromAge, max: toAge })
    }

    if ((fromAge > toAge && toAge !== null) || fromAge === toAge) {
      setToAgeError(true)
    }

    if (toAge > fromAge && toAge <= MAX_AGE && fromAge >= MIN_AGE) {
      setToAgeError(false)
    }
  }, [fromAge, toAge])

  useEffect(() => {
    if (!isAgeLimitToggleEnabled && fromAge && toAge) {
      setFromAge(null)
      setToAge(null)
      setFromAgeError(false)
      setToAgeError(false)
      handleMembershipValueChange('membership_age_range_limit', { min: null, max: null })
    }

    if (!isAgeLimitToggleEnabled) {
      setFromAgeError(false)
      setToAgeError(false)
    }
  }, [isAgeLimitToggleEnabled, fromAge, toAge])

  const getError = (field) => {
    return errors && errors[field] ? errors[field] : ''
  }

  const handleNoEnd = (value) => {
    setIsNoEnd(value)
    handleDateChange('to', value ? null : membershipValue.duration.to)
  }

  const handleDescriptionChange = (value) => {
    if (value.length > DESCRIPTION_MAX_LENGTH) {
      value = value.substring(0, DESCRIPTION_MAX_LENGTH)
    }
    setDescriptionText(value)
    handleMembershipValueChange('description', value)
  }
  const handleAutoRenewalDaysChange = (key, value, minAndMaxValues) => {
    handleMembershipValueChange('autorenewal', {
      ...membershipValue.autorenewal,
      [key]: value,
    })
  }

  const toggleAutoRenewal = (value) => {
    const updatedValues = {
      ...membershipValue.autorenewal,
      is_active: value,
    }

    // In case of the initial toggle on, we need to be sure that
    // the days_before_cancellation and days_before_email have values.
    if (value) {
      // If days_before_cancellation does not have a value, set it to the minimum value.
      if (!updatedValues.days_before_cancellation) {
        updatedValues.days_before_cancellation = AUTO_RENEWAL_LAST_CANCELLATION_MIN_MAX_DAYS.min
      }

      // If days_before_email does not have a value, set it to the minimum value.
      if (!updatedValues.days_before_email) {
        updatedValues.days_before_email = AUTO_RENEWAL_EMAIL_TO_MEMBER_MIN_MAX_DAYS.min
      }
    }
    handleMembershipValueChange('autorenewal', updatedValues)
  }

  const handleCancel = () => {
    if (!showActionButtons) return
    setShowActionButtons(false)
    setSubscriptionName(currentMembership?.name || '')
    handleMembershipValueChange('name', currentMembership?.name || '')
  }

  const handleDateChange = (key, date) => {
    if (date) {
      date = moment(date).utc().startOf('day').format()
    }
    handleMembershipValueChange('duration', {
      ...membershipValue.duration,
      [key]: date?.toString() || null,
    })
  }

  const handleRequirementUpdate = (key, value) => {
    const reqs = [...membershipValue.membership_requirements]
    const index = reqs.findIndex((obj) => obj.type === key)
    if (index > -1) {
      reqs[index] = {
        ...reqs[index],
        configuration: { ...reqs[index].configuration, setting: value },
      }
    } else {
      reqs.push({ type: key, configuration: { setting: value } })
    }
    handleMembershipValueChange('membership_requirements', reqs)
  }

  const handleMaxSeatsToggle = (value) => {
    setIsMaxSeatsToggleEnabled(value)
    let membership_limit_copy = { ...membershipValue.membership_member_limit }
    if (value) {
      if (
        (membershipValue?.membership_member_limit?.max_members === 0 &&
          membershipValue?.membership_member_limit?.current_members > 0) ||
        membershipValue?.membership_member_limit?.current_members >
          membershipValue?.membership_member_limit?.max_members
      ) {
        membership_limit_copy.max_members =
          membershipValue?.membership_member_limit?.current_members
        setMaxSeatsValue(membershipValue?.membership_member_limit?.current_members)
      }
    }

    handleMembershipValueChange('membership_member_limit', {
      ...membership_limit_copy,
      is_active: value,
    })
  }

  const onMaxMembersChange = (value) => {
    if ((value) => 0 && value < 10000) {
      if (value < numberOfCurrentMembers) {
        setMaxSeatsValue(value)
      } else {
        const maxMembersPayload = {
          is_active: isMaxSeatsToggleEnabled,
          max_members: value,
        }

        handleMembershipValueChange('membership_member_limit', maxMembersPayload)
      }
    }
  }

  const handleFromAgeChange = (value) => {
    const numericValue = Math.min(MAX_AGE, Math.max(0, Number(value) || 0))
    setFromAge(numericValue)
  }

  const handleToAgeChange = (value) => {
    const numericValue = Math.min(MAX_AGE, Math.max(0, Number(value) || 0))
    setToAge(numericValue)
  }

  const renderFields = () => {
    return (
      <React.Fragment>
        <TextInputField
          containerWidth={'full'}
          label={TRANSLATIONS.nameOfMembership}
          lightMainLabelColor
          placeholder={TRANSLATIONS.name}
          value={subscriptionName}
          error={getError('name')}
          onChange={(val) => {
            setSubscriptionName(val)
            setSingleEntityValue('name', val)
          }}
          type="text"
          onInputFocus={() => setShowActionButtons(true)}
          inputProps={{ autoFocus: !loading && !currentMembership?.name }}
          showActionButtons={!subscriptionName || showActionButtons}
          showConfirmButton
          confirmText={currentMembership?.name ? TRANSLATIONS.save : TRANSLATIONS.create}
          showCancelButton={!!subscriptionName}
          onConfirm={() => {
            setShowActionButtons(false)
            handleOnSave()
          }}
          onCancel={() => handleCancel()}
          confirmLoading={loading}
        />
        {!disabled && (
          <React.Fragment>
            <div className={styles.descriptionLabelRow}>
              <Label label={TRANSLATIONS.description} lightColor></Label>
              <Label
                label={`${descriptionText.length}/${DESCRIPTION_MAX_LENGTH}`}
                lightColor
              ></Label>
            </div>
            <TextInputField
              containerWidth={'85%'}
              rows={5}
              lightMainLabelColor
              value={descriptionText}
              // error={getError('name')}
              onChange={(value) => handleDescriptionChange(value)}
              onInputBlur={() => handleMembershipValueChange('description', descriptionText)}
              type="text-area"
              disabled={disabled}
            />

            <ToggleButton
              value={membershipValue.play_right_only}
              label={TRANSLATIONS.playRight}
              secondaryLabel={TRANSLATIONS.required}
              error={getError('play_right_only')}
              onChange={(val) => handleMembershipValueChange('play_right_only', val)}
              disabled={disabled}
            />
            <ToggleButton
              value={membershipValue.duration.options.includes('annual_duration')}
              label={TRANSLATIONS.duration}
              secondaryLabel={TRANSLATIONS.annual}
              error={getError('duration')}
              onChange={(newValue) => setDurationValue('annual_duration', newValue)}
              disabled={disabled || membershipValue.duration.options.includes('period')}
            />
            <ToggleButton
              value={membershipValue.duration.options.includes('12_month')}
              secondaryLabel={TRANSLATIONS.twelve_months}
              error={getError('duration')}
              onChange={(newValue) => setDurationValue('12_month', newValue)}
              disabled={disabled || membershipValue.duration.options.includes('period')}
              lightLabel
            />
            <ToggleButton
              value={membershipValue.duration.options.includes('period')}
              secondaryLabel={TRANSLATIONS.period}
              error={getError('duration')}
              onChange={(newValue) => setDurationValue('period', newValue)}
              disabled={
                disabled ||
                membershipValue.autorenewal?.is_active ||
                membershipValue.duration.options.includes('annual_duration') ||
                membershipValue.duration.options.includes('12_month')
              }
            />
            {membershipValue.duration.options.includes('period') && (
              <div className={cx(styles.periodContainer)}>
                <div>
                  <Label label={TRANSLATIONS.startDate} lightColor />
                </div>
                <div>
                  <InputSelectDateSimple
                    className={cx(styles.dateInput)}
                    currentDate={moment(membershipValue.duration.from) ?? moment.utc()}
                    onSetDate={(value) => handleDateChange('from', value)}
                    disableBeforeToday={true}
                    orientation={'right'}
                    startDate={moment().utc()}
                    endDate={
                      membershipValue.duration.to
                        ? moment(membershipValue.duration.to).subtract(1, 'day')
                        : moment().add(10, 'years')
                    }
                    value={
                      membershipValue.duration.from !== null
                        ? moment(membershipValue.duration.from).format('YYYY-MM-DD')
                        : moment.utc().startOf('day').format('YYYY-MM-DD')
                    }
                  />
                </div>
                <div>
                  <Label label={TRANSLATIONS.endDate} lightColor />
                </div>
                <div>
                  <InputSelectDateSimple
                    className={cx(styles.dateInput)}
                    currentDate={
                      membershipValue.duration.to !== null
                        ? moment(membershipValue.duration.to)
                        : membershipValue.duration.from !== null
                        ? moment(membershipValue.duration.from)
                        : moment()
                    }
                    onSetDate={(value) => handleDateChange('to', value)}
                    disableBeforeToday={true}
                    placeholder={isNoEnd ? TRANSLATIONS.noEnd : TRANSLATIONS.chooseDate}
                    orientation={'right'}
                    startDate={moment(membershipValue.duration.from).add(1, 'day')}
                    endDate={moment().add(10, 'years')}
                    value={
                      membershipValue.duration.to !== null
                        ? moment(membershipValue.duration.to).format('YYYY-MM-DD')
                        : ''
                    }
                    disabled={isNoEnd}
                  />
                </div>
                {/* <div className={}> */}
                <ToggleButton
                  value={isNoEnd}
                  secondaryLabel={TRANSLATIONS.noEnd}
                  error={getError('noend')}
                  containerWidth="quarter"
                  onChange={(value) => handleNoEnd(value)}
                  containerClassName={cx(styles.toggleEndDate)}
                />
                {/* </div> */}
              </div>
            )}

            {values?.type === 'membership' && (
              <>
                <ToggleButton
                  value={!!membershipValue.is_git_sync}
                  label={TRANSLATIONS.gitSync}
                  secondaryLabel={TRANSLATIONS.required}
                  error={getError('play_right_only')}
                  onChange={(newValue) => handleMembershipValueChange('is_git_sync', newValue)}
                  disabled={disabled}
                  lightLabel
                />
                {!!currentClub.git_id && (
                  <>
                    <ToggleButton
                      value={
                        !!membershipValue.membership_requirements?.find(
                          (req) => req.type === 'golf_id'
                        )?.configuration?.setting
                      }
                      secondaryLabel={t('memberships.label_GolfIdRequired')}
                      error={getError('membership_requirements')}
                      onChange={(newValue) => handleRequirementUpdate('golf_id', newValue)}
                      disabled={disabled}
                      lightLabel
                    />
                    {isGitClubSelectEnabled && (
                      <ToggleButton
                        value={enableGitCLubSelect ?? membershipValue.is_git_club_selectable}
                        secondaryLabel={t('memberships.label_GitClubInfo')}
                        error={getError('membership_git_club')}
                        onChange={() => toggleGitClubSelect.mutate()}
                        disabled={disabled}
                        lightLabel
                      />
                    )}
                  </>
                )}
              </>
            )}
            <ToggleButton
              label="Settings"
              value={!!membershipValue.is_sell_membership_enabled}
              secondaryLabel={
                values?.type === 'membership'
                  ? t('memberships.label_SellThroughSweetspot')
                  : t('memberships.label_SellPassThroughSweetspot')
              }
              error={getError('is_sell_membership_enabled')}
              onChange={(newValue) =>
                handleMembershipValueChange('is_sell_membership_enabled', newValue)
              }
              disabled={disabled}
              lightLabel
            />
            {!!membershipValue.is_sell_membership_enabled && (
              <div className="flex flex-col">
                <span className="text-text-subtle text-xs font-bold">
                  {t('membership.hidePublic')}
                </span>
                <span className="text-text-medium text-xs">{t('membership.hidePublicDesc')}</span>

                <RadioGroup.Root
                  className="w-min gap-1 pb-3 pt-2"
                  onValueChange={(value) => {
                    handleMembershipValueChange('public', value === 'public')
                  }}
                  value={membershipValue.public}
                  defaultValue={membershipValue.public}
                  disabled={disabled}
                >
                  <RadioGroup.Item
                    value="public"
                    id="public"
                    label={t('words.public')}
                    isActive={membershipValue.public === true}
                    disabled={disabled}
                    className="[&>label]:text-text-subtle [&>span]:text-text-subtle [&>i]:text-text-subtle h-min min-h-min justify-start gap-3 px-0 text-sm [&>label]:text-sm"
                  />
                  <RadioGroup.Item
                    value="private"
                    id="private"
                    label={t('words.private')}
                    isActive={membershipValue.public === false}
                    disabled={disabled}
                    className="[&>label]:text-text-subtle [&>span]:text-text-subtle [&>i]:text-text-subtle h-min min-h-min justify-start gap-3 px-0 text-sm [&>label]:text-sm"
                  />
                </RadioGroup.Root>
              </div>
            )}

            <ToggleButton
              value={membershipValue.autorenewal?.is_active}
              secondaryLabel={t('memberships.label_auto_renew_subscription')}
              error={getError('autorenewal')}
              onChange={(newValue) => toggleAutoRenewal(newValue)}
              disabled={disabled || membershipValue.duration.options.includes('period')}
              lightLabel
            />
            {membershipValue.autorenewal?.is_active && (
              <div className={cx(styles.membershipFieldsContainer)}>
                <div className={cx(styles.membershipField)}>
                  <Label label={t('memberships.label_last_cancellation_date')} lightColor />
                  <div className={cx(styles.membershipInputWithSuffix)}>
                    <TextInputField
                      lightMainLabelColor
                      value={membershipValue.autorenewal?.days_before_cancellation}
                      error={getError('autorenewal')}
                      onChange={(newValue) =>
                        handleAutoRenewalDaysChange(
                          'days_before_cancellation',
                          newValue,
                          AUTO_RENEWAL_LAST_CANCELLATION_MIN_MAX_DAYS
                        )
                      }
                      type="number"
                      disabled={disabled}
                      noMargin
                      inputProps={AUTO_RENEWAL_LAST_CANCELLATION_MIN_MAX_DAYS}
                    />
                    <Label label={t('memberships.label_last_cancellation_date_suffix')} />
                  </div>
                </div>
                <div className={cx(styles.membershipField)}>
                  <div className={cx(styles.emailToMemberLabelContainer)}>
                    <Label label={t('memberships.label_email_to_member')} lightColor />
                    <InfoHover
                      text={t('memberships.email_to_member_tooltip')}
                      containerClassName={cx(styles.emailToMemberTooltipContainer)}
                      arrowPosition={5}
                    />
                  </div>
                  <div className={cx(styles.membershipInputWithSuffix)}>
                    <TextInputField
                      lightMainLabelColor
                      value={membershipValue.autorenewal?.days_before_email}
                      error={getError('autorenewal')}
                      onChange={(newValue) =>
                        handleAutoRenewalDaysChange(
                          'days_before_email',
                          newValue,
                          AUTO_RENEWAL_EMAIL_TO_MEMBER_MIN_MAX_DAYS
                        )
                      }
                      type="number"
                      disabled={disabled}
                      noMargin
                      inputProps={AUTO_RENEWAL_EMAIL_TO_MEMBER_MIN_MAX_DAYS}
                    />
                    <Label label={t('memberships.label_email_to_member_suffix')} />
                  </div>
                </div>
              </div>
            )}
            <ToggleButton
              value={isMaxSeatsToggleEnabled}
              secondaryLabel={t('membership.settings.maxSeatsAllowed')}
              secondaryLabelInfo={t('membership.infoHoverText.seats_limit')}
              onChange={(newValue) => handleMaxSeatsToggle(newValue)}
              disabled={disabled}
              lightLabel
            />
            {isMaxSeatsToggleEnabled && (
              <div className="mb-4 flex h-8 gap-2">
                <TextInputField
                  containerWidth={'15%'}
                  placeholder="Max 9999"
                  lightMainLabelColor
                  value={maxSeatsValue}
                  onChange={(value) => onMaxMembersChange(value)}
                  type="number"
                  inputProps={MAX_MEMBERSHIP_SEATS_ALLOWEED}
                  noMargin
                />
                {numberOfCurrentMembers !== null && numberOfCurrentMembers > 0 && (
                  <span className="text-xs text-[#A6ACB5]">
                    {t('wizard.membershipMaxSeatsInfo', {
                      current_members: numberOfCurrentMembers,
                    })}
                  </span>
                )}
              </div>
            )}
            <ToggleButton
              value={isAgeLimitToggleEnabled}
              onChange={(newValue) => setIsAgeLimitToggleEnabled(newValue)}
              secondaryLabel={t('membership.settings.limitAge')}
              disabled={disabled}
              lightLabel
            />
            {isAgeLimitToggleEnabled && (
              <div className="mb-4 flex h-8 gap-2">
                <TextInputField
                  containerWidth={'15%'}
                  placeholder={t('membership.settings.fromAge')}
                  lightMainLabelColor
                  type="number"
                  error={fromAgeError && t('membership.settings.wrongInput')}
                  noMargin
                  value={fromAge}
                  onChange={(value) => handleFromAgeChange(value)}
                  min={MIN_AGE.toString()}
                  max={MAX_AGE.toString()}
                />
                <TextInputField
                  containerWidth={'15%'}
                  placeholder={t('membership.settings.toAge')}
                  lightMainLabelColor
                  type="number"
                  error={toAgeError && t('membership.settings.wrongInput')}
                  noMargin
                  value={toAge}
                  onChange={(value) => handleToAgeChange(value)}
                  min={MIN_AGE.toString()}
                  max={MAX_AGE.toString()}
                />
              </div>
            )}
            <MigrateButton
              autorenewal={membershipValue.autorenewal}
              membershipId={membershipValue.id}
              migrateMembershipAutorenewalSettings={migrateMembershipAutorenewalSettings}
            />
            <div className={cx(styles.toggleWrapper, 'mb-1')}>
              <ToggleSwitchControlled
                switchWidth="full"
                label={TRANSLATIONS.membershipStatus}
                error={getError('is_active')}
                checked={!!membershipValue.is_active}
                onChange={(newValue) => setSingleEntityValue('is_active', newValue)}
                textChecked={TRANSLATIONS.active}
                textUnchecked={TRANSLATIONS.inactive}
                disabled={disabled}
                medium
              />
            </div>
            {values?.is_sell_membership_enabled ? (
              <div className="flex w-full items-center justify-center pb-6">
                <p className="max-w-xl text-center text-xs text-red-400">
                  {t('memberships.message_sell_membership_activate_information_first')}
                  <br />
                  {t('memberships.message_sell_membership_activate_information_second')}
                  <br />
                  {t('memberships.message_sell_membership_activate_information_third')}{' '}
                  <a href="mailto:hello@sweetspot.io">
                    <u className="text-red-400">
                      {t('memberships.message_sell_membership_activate_information_fourth')}
                    </u>
                  </a>
                </p>
              </div>
            ) : null}
          </React.Fragment>
        )}
      </React.Fragment>
    )
  }

  if (onlyFields === true) return renderFields()

  return (
    <React.Fragment>
      <div className={cx(styles.container)}>{renderFields()}</div>
      {shouldRenderUpdatedUI && (
        <div className="sticky bottom-0 flex w-full justify-end bg-white p-4">
          <Button
            disabled={isLoading}
            variant="inverted"
            className="text-sm"
            onClick={onCloseModal}
          >
            {t('cancel')}
          </Button>
          <Button disabled={isLoading} className="ml-3 text-sm" onClick={handleSaveBtnClick}>
            {isLoading ? <FontAwesomeIcon icon={faSpinnerThird} spin /> : t('save')}
          </Button>
        </div>
      )}
    </React.Fragment>
  )
}

GeneralSettings.propTypes = {
  /** From Redux State */
  lang: PropTypes.string,
  /** From Parent */
  values: PropTypes.object.isRequired,
  errors: PropTypes.object,
  onSetMembershipValue: PropTypes.func,
  handleOnSave: PropTypes.func,
  migrateMembershipAutorenewalSettings: PropTypes.func,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  onlyFields: PropTypes.bool,
  currentMembership: PropTypes.object,
}

GeneralSettings.defaulProps = {
  values: {},
  currentMembership: {},
  errors: {},
  onSetMembershipValue: () => {},
  migrateMembershipAutorenewalSettings: () => {},
  disabled: false,
  loading: false,
  onlyFields: false,
}

const mapStateToProps = (state) => ({
  lang: state.auth.me.lang,
})

export default connect(mapStateToProps)(GeneralSettings)
